import React, { useEffect } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import MainLayout from '@app/layouts/MainLayout';
import Profiling from '@app/pages/Profiling';
import Searching from '@app/pages/Searching';
import Showcase from '@app/pages/stepper/showcase/index';
import { JobProgressSwitchNotFound } from '@components/navigation/JobProgressSwitch/_partials/JobProgressNotFound';
import { JobProgressSwitchSegmentWrapper } from '@components/navigation/JobProgressSwitch/_partials/JobProgressSwitchSegmentWrapper';
import Interviewing from '@app/pages/Interviewing';
import Closing from '@app/pages/Closing';
import { useEntityNavigation } from '@components/navigation/JobProgressSwitch/_hooks/use-entity-navigation';
import { useJobProgress } from '@utils/hooks/use-job-progress';
import { useStoreState } from '@app/store/hooks/index';
import { jobStoreSelector } from '@model/Job/Job.selectors';
import { LoaderFullPage } from '@app/loaders/LoaderFullPage';
import {
  JOB_PROGRESS_SWITCH_SEGMENT_ENTITIES,
  TJobProgressSwitchSegmentEntity,
} from '@components/navigation/JobProgressSwitch/JobProgressSwitch.types';
import { ErrorScreen } from '@components/ErrorScreen/index';
import { useTranslation } from 'react-i18next';

const JobProgressSwitch: React.FC = () => {
  const jobProgress = useJobProgress();
  const { entityType } = useParams();
  const { t } = useTranslation();

  // STATE
  const job = useStoreState(jobStoreSelector);

  useEntityNavigation();

  const isValidEntityType =
    entityType &&
    [
      JOB_PROGRESS_SWITCH_SEGMENT_ENTITIES.JOBS,
      JOB_PROGRESS_SWITCH_SEGMENT_ENTITIES.CONTRACTS,
    ].includes(entityType as TJobProgressSwitchSegmentEntity);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  if (!isValidEntityType) {
    return (
      <ErrorScreen
        className="c-error-screen--unauthorized"
        title={t('general.error.unauthorizedHomepage.title') ?? ''}
      />
    );
  }

  if (!job) {
    return <LoaderFullPage />;
  }

  return (
    <MainLayout>
      <Routes>
        <Route
          path="/profiling"
          element={
            <JobProgressSwitchSegmentWrapper loaderType="profiling">
              <Profiling />
            </JobProgressSwitchSegmentWrapper>
          }
        />

        <Route
          path="/searching"
          element={
            <JobProgressSwitchSegmentWrapper loaderType="searching">
              <Searching />
            </JobProgressSwitchSegmentWrapper>
          }
        />

        <Route
          path="/interviewing"
          element={
            <JobProgressSwitchSegmentWrapper loaderType="interviewing">
              <Interviewing />
            </JobProgressSwitchSegmentWrapper>
          }
        />

        <Route
          path="/closing"
          element={
            <JobProgressSwitchSegmentWrapper loaderType="closing">
              <Closing />
            </JobProgressSwitchSegmentWrapper>
          }
        />

        <Route
          path="/showcase"
          element={job?.isShowcaseEnabled ? <Showcase /> : null}
        />

        <Route
          path="/*?"
          element={<JobProgressSwitchNotFound progress={jobProgress} />}
        />
      </Routes>
    </MainLayout>
  );
};

export default JobProgressSwitch;
